import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { REGISTER } from "@/constants/testIds";
import { Sprout, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await register({ company_name: form.company_name, name: form.name, email: form.email, password: form.password });
      toast.success("Tenant created — you are the administrator");
      navigate("/");
    } catch (err) {
      setError(apiErr(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-md animate-fade-up rounded-2xl border border-sage-light bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-forest-mid">
            <Sprout className="h-5 w-5 text-white" />
          </div>
          <span className="font-heading text-xl font-bold text-forest-dark">ROOTS</span>
        </div>
        <h2 className="font-heading text-2xl font-bold text-forest-dark">Create your company</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          You'll be set up as the tenant administrator and can invite your team.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="company">Company name</Label>
            <Input id="company" value={form.company_name} onChange={set("company_name")} required
              placeholder="GreenScape Pte Ltd" className="mt-1" data-testid="register-company-input" />
          </div>
          <div>
            <Label htmlFor="name">Your name</Label>
            <Input id="name" value={form.name} onChange={set("name")} required className="mt-1" data-testid={REGISTER.nameInput} />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={set("email")} required className="mt-1" data-testid={REGISTER.emailInput} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required className="mt-1" data-testid={REGISTER.passwordInput} />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm</Label>
              <Input id="confirm" type="password" value={form.confirm} onChange={set("confirm")} required className="mt-1" data-testid={REGISTER.passwordConfirmInput} />
            </div>
          </div>
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-forest-mid hover:bg-forest-dark" data-testid={REGISTER.submitButton}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create company
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-forest-mid hover:underline" data-testid={REGISTER.loginLink}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
